import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useCareCreditLocationStatus } from '@frontend/payments-care-credit';
import { PaymentsFeatureFlags, useMerchants } from '@frontend/payments-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { StaticSettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Heading, IndexRow } from '@frontend/design-system';
import * as settingStyles from '../styles/payment-settings-page.styles';

const styles = {
  indexRow: css`
    & > article {
      max-width: none;
      width: 100%;
    }
  `,
};

export const MultiPaymentSettings = () => {
  const { t } = useTranslation('payments', { keyPrefix: 'settings' });
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { anyLocationHasPayments, anyLocationHasFullPayments } = useMerchants(locationIds);
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const navigate = (to: StaticSettingsRoutes) => settingsNavigate({ to });

  const { getFeatureFlagValue } = useAppFlagStore();
  const hasJustifyOnboardingEnabled = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableJustifyOnboarding,
    locationIds,
  });

  const { isCareCreditEnabled } = useCareCreditLocationStatus(locationIds[0]);

  return (
    <>
      <section css={[settingStyles.groupMaxWidth, styles.indexRow]}>
        <Heading level={3}>{t('Account Setup')}</Heading>
        <IndexRow>
          <IndexRow.Item onClick={() => navigate(`/payments/account-info`)}>
            <IndexRow.Item.Title>{t('Account Info and Status')}</IndexRow.Item.Title>
          </IndexRow.Item>
          {anyLocationHasPayments && (
            <IndexRow.Item onClick={() => navigate(`/payments/public-business-information`)}>
              <IndexRow.Item.Title>{t('Public Business Information')}</IndexRow.Item.Title>
            </IndexRow.Item>
          )}
          {hasJustifyOnboardingEnabled && (
            <IndexRow.Item onClick={() => navigate(`/payments/justifi-settings`)}>
              <IndexRow.Item.Title>{t('JustiFi Setup')}</IndexRow.Item.Title>
            </IndexRow.Item>
          )}
        </IndexRow>
        {anyLocationHasPayments && (
          <>
            <Heading level={3} css={{ marginTop: theme.spacing(2) }}>
              {t('Payment Collection')}
            </Heading>
            <IndexRow>
              {anyLocationHasFullPayments && (
                <IndexRow.Item onClick={() => navigate(`/payments/terminals`)}>
                  <IndexRow.Item.Title>{t('Terminals')}</IndexRow.Item.Title>
                </IndexRow.Item>
              )}
              <IndexRow.Item onClick={() => navigate(`/payments/payment-methods`)}>
                <IndexRow.Item.Title>{t('Payment Methods')}</IndexRow.Item.Title>
              </IndexRow.Item>
              <IndexRow.Item onClick={() => navigate(`/payments/online-bill-pay`)}>
                <IndexRow.Item.Title>{t('Online Bill Pay')}</IndexRow.Item.Title>
              </IndexRow.Item>
              <IndexRow.Item onClick={() => navigate(`/payments/general-settings`)}>
                <IndexRow.Item.Title>{t('General Settings')}</IndexRow.Item.Title>
              </IndexRow.Item>
              <IndexRow.Item onClick={() => navigate(`/payments/sunbit`)}>
                <IndexRow.Item.Title>{t('Sunbit')}</IndexRow.Item.Title>
              </IndexRow.Item>
              {isCareCreditEnabled && (
                <IndexRow.Item onClick={() => navigate(`/payments/care-credit`)}>
                  <IndexRow.Item.Title>{t('CareCredit')}</IndexRow.Item.Title>
                </IndexRow.Item>
              )}
            </IndexRow>
          </>
        )}
      </section>
    </>
  );
};
